import { svg, esc } from '../ui/icons.js';
import { competenciaExtenso } from '../lib/supabase.js';
import * as Fechamento from '../data/fechamento.js';
import * as Comentarios from '../data/comentarios.js';
import * as EmpresasData from '../data/empresas.js';
import { SITUACOES } from '../data/empresas.js';
import { dados, podeEditar, recarregarFechamento, filiaisDe } from '../state.js';
import { usuario, ehGestao } from '../auth.js';

/**
 * Ficha da empresa. As abas de cadastro são leitura; a aba Fechamento grava.
 *
 * Diferenças em relação ao protótipo:
 *  1. O checklist sabe QUAIS itens estão marcados (antes era só um contador).
 *  2. Dá para selecionar VÁRIAS competências e trabalhar nelas de uma vez —
 *     o caso de pegar a empresa atrasada e fazer janeiro a agosto numa
 *     sentada só. Status aplica em todas; a observação vai só para o mês
 *     mais recente selecionado.
 *  3. Cada item tem comentários, para a revisão explicar o que aconteceu.
 *  4. Quem não é responsável vê tudo mas não edita — e a RLS confirma isso
 *     no banco, não só na tela.
 */

const ABAS = [
  { id: 'cadastro', label: 'Cadastro' },
  { id: 'pessoal', label: 'Pessoal' },
  { id: 'open', label: 'Open/Bancos' },
  { id: 'documentacao', label: 'Documentação' },
  { id: 'andamento', label: 'Andamento' },
  { id: 'fechamento', label: 'Fechamento' },
  { id: 'particularidades', label: 'Particularidades' },
];

let aoMudarDados = () => {};
let empresaAtual = null;
let competencias = [];        // todas as competências da empresa
let selecionadas = new Set(); // ids dos fechamentos marcados

export function configurarFicha({ onChange }) {
  aoMudarDados = onChange || (() => {});
  document.getElementById('fichaClose').addEventListener('click', fecharFicha);
  document.getElementById('overlayScrim').addEventListener('click', fecharFicha);
}

export function abrirFicha(empresaId, abaInicial = 'cadastro') {
  editando.clear();
  const e = dados.empresas.find((x) => x.id === empresaId);
  if (!e) return;
  empresaAtual = e;

  const responsaveis = (e.responsaveis || []).map((r) => r.nome).join(', ') || '—';
  document.getElementById('fichaNome').textContent = e.nome;
  document.getElementById('fichaMeta').textContent =
    `${e.cnpj || '—'} · ${e.regime || '—'} · Responsável: ${responsaveis}`;

  // Na filial o contábil é centralizado na matriz, então a aba Fechamento
  // não faz sentido ali — apontar para a matriz evita trabalho duplicado.
  const abas = e.matriz_id ? ABAS.filter((t) => t.id !== 'fechamento') : ABAS;
  const primeira = abas.some((t) => t.id === abaInicial) ? abaInicial : 'cadastro';

  document.getElementById('fichaTabs').innerHTML = abas.map((t) =>
    `<button class="ficha-tab ${t.id === primeira ? 'active' : ''}" data-tab="${t.id}">${t.label}</button>`
  ).join('');

  document.querySelectorAll('.ficha-tab').forEach((b) =>
    b.addEventListener('click', () => {
      document.querySelectorAll('.ficha-tab').forEach((x) => x.classList.toggle('active', x === b));
      renderAba(e, b.dataset.tab);
    })
  );

  renderAba(e, primeira);
  document.getElementById('overlayScrim').classList.add('show');
  document.getElementById('fichaPanel').classList.add('show');
}

export function fecharFicha() {
  document.getElementById('overlayScrim').classList.remove('show');
  document.getElementById('fichaPanel').classList.remove('show');
}

/* ------------------------------------------------------------------ */
/* Abas de leitura                                                     */
/* ------------------------------------------------------------------ */

const campo = (k, v) => `<div class="field"><p class="k">${k}</p><p class="v">${esc(v ?? '—')}</p></div>`;
const simNao = (v) => (v ? 'Sim' : 'Não');
const dataBR = (d) => (d ? new Date(d + 'T00:00').toLocaleDateString('pt-BR') : '—');

/* ------------------------------------------------------------------ */
/* Situação do cliente                                                 */
/*                                                                     */
/* O período tem os dois lados de propósito: um cliente que entrou em   */
/* abril não pode ganhar competências de janeiro a março quando alguém  */
/* abrir o ano em lote.                                                */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/* Responsáveis                                                        */
/*                                                                     */
/* Mais de um de propósito: quando a demanda da empresa é grande, a     */
/* carteira se divide. Só a gestão mexe — a RLS confirma isso no banco. */
/* ------------------------------------------------------------------ */

function blocoResponsaveis(e) {
  const atuais = new Set((e.responsaveis || []).map((r) => r.id));

  return `
    <details class="situacao-editor resp-editor" ${atuais.size ? '' : 'open'}>
      <summary>Responsáveis pela empresa${atuais.size ? ` (${atuais.size})` : ' — nenhum definido'}</summary>
      <p class="resp-ajuda">
        Marque quem responde por esta empresa. Pode ser mais de um quando a
        demanda exigir dividir. Quem não está marcado vê os dados, mas não edita.
      </p>
      <div class="resp-lista">
        ${dados.colaboradores.map((c) => `
          <label class="resp-item">
            <input type="checkbox" data-resp="${c.id}" ${atuais.has(c.id) ? 'checked' : ''}>
            <span>${esc(c.nome)}</span>
            <span class="resp-papel">${c.papel === 'gestao' ? 'Gestão' : 'Colaborador'}</span>
          </label>`).join('')}
      </div>
      <p class="situacao-aviso" id="respAviso" hidden></p>
    </details>`;
}

/**
 * Editor de contas bancárias.
 *
 * Existe porque a varredura das pastas criou conta demais: o rótulo de
 * uma pasta não é o nome de uma conta. A ACP saiu com sete, e são três ou
 * quatro — "EXTRATOS BRADESCO 2025" é organização por ano, não conta.
 *
 * A gestão decidiu que a limpeza é da equipe, conta por conta, enquanto
 * usa. Sem esta tela isso seria impossível: até aqui a ficha só exibia os
 * bancos, e não havia como somar nem tirar nenhum.
 *
 * Remover é baixa lógica. Os documentos já marcados continuam presos à
 * conta; apagar a linha levaria junto o registro de que o extrato de
 * março chegou.
 */
function blocoContas(e) {
  const contas = (e.bancos || []).filter((b) => b.ativo !== false);
  const editavel = podeEditar(e, usuario());
  const doConciliador = (b) => b.agencia || b.conta;

  return `
    <details class="situacao-editor resp-editor" open>
      <summary>Contas bancárias (${contas.length})</summary>
      <p class="resp-ajuda">
        O extrato em PDF é obrigatório em toda conta, esteja ela no Open Finance ou não.
        ${contas.some((b) => !doConciliador(b)) ? `
          As contas sem agência vieram do nome da pasta na rede e podem estar repetidas
          ou não ser conta nenhuma — confira e remova o que não for.` : ''}
      </p>
      <div class="resp-lista">
        ${contas.length ? contas.map((b) => `
          <div class="resp-item conta-item" data-conta="${b.id}">
            ${editavel
              ? `<input class="cad-input conta-nome" value="${esc(b.nome)}" data-antes="${esc(b.nome)}">`
              : `<span>${esc(b.nome)}</span>`}
            <span class="resp-papel">
              ${doConciliador(b)
                ? `Ag ${esc(b.agencia || '—')} · ${esc(b.conta || '—')}`
                : 'sem agência'}${b.open_finance ? ' · Open' : ''}
            </span>
            ${editavel ? '<button type="button" class="conta-remover" title="Remover conta">Remover</button>' : ''}
          </div>`).join('')
          : '<p class="resp-ajuda">Nenhuma conta cadastrada.</p>'}
      </div>
      ${editavel ? `
        <div class="conta-nova">
          <input class="cad-input" id="contaNova" placeholder="Nome do banco (ex.: Itaú, Bradesco Aplicação)">
          <button type="button" id="contaAdicionar">Adicionar</button>
        </div>` : ''}
      <p class="situacao-aviso" id="contaAviso" hidden></p>
    </details>`;
}

function ligarContas(empresa, body) {
  const aviso = body.querySelector('#contaAviso');
  const mostrar = (texto, erro = false) => {
    aviso.textContent = texto;
    aviso.hidden = !texto;
    aviso.classList.toggle('erro', erro);
  };

  body.querySelectorAll('.conta-nome').forEach((campoNome) =>
    campoNome.addEventListener('change', async () => {
      const id = campoNome.closest('[data-conta]').dataset.conta;
      const novo = campoNome.value.trim();
      if (!novo || novo === campoNome.dataset.antes) { campoNome.value = campoNome.dataset.antes; return; }
      try {
        await EmpresasData.renomearBanco(id, novo);
        campoNome.dataset.antes = novo;
        mostrar('Nome da conta atualizado.');
        await aoMudarDados();
      } catch (erro) {
        campoNome.value = campoNome.dataset.antes;
        mostrar(erro.message, true);
      }
    })
  );

  body.querySelectorAll('.conta-remover').forEach((botao) =>
    botao.addEventListener('click', async () => {
      const linha = botao.closest('[data-conta]');
      const nome = linha.querySelector('.conta-nome')?.value || linha.textContent.trim();
      if (!confirm(`Remover a conta "${nome}"?\n\nOs extratos já marcados nela continuam registrados no histórico.`)) return;
      botao.disabled = true;
      try {
        await EmpresasData.removerBanco(linha.dataset.conta);
        linha.remove();
        mostrar('Conta removida.');
        await aoMudarDados();
      } catch (erro) {
        botao.disabled = false;
        mostrar(erro.message, true);
      }
    })
  );

  const botaoAdicionar = body.querySelector('#contaAdicionar');
  if (botaoAdicionar) {
    botaoAdicionar.addEventListener('click', async () => {
      const campoNovo = body.querySelector('#contaNova');
      const nome = campoNovo.value.trim();
      if (!nome) return;
      botaoAdicionar.disabled = true;
      try {
        await EmpresasData.adicionarBanco(empresa.id, { nome });
        campoNovo.value = '';
        mostrar(`Conta "${nome}" adicionada.`);
        await aoMudarDados();
      } catch (erro) {
        mostrar(erro.message, true);
      } finally {
        botaoAdicionar.disabled = false;
      }
    });
  }
}

function ligarResponsaveis(empresa, body) {
  body.querySelectorAll('[data-resp]').forEach((chk) =>
    chk.addEventListener('change', async () => {
      const marcados = [...body.querySelectorAll('[data-resp]')]
        .filter((x) => x.checked)
        .map((x) => x.dataset.resp);

      const aviso = body.querySelector('#respAviso');
      try {
        await EmpresasData.definirResponsaveis(empresa.id, marcados);
        await recarregarFechamento();
        await aoMudarDados();

        const atualizada = dados.empresas.find((x) => x.id === empresa.id) || empresa;
        const nomes = (atualizada.responsaveis || []).map((r) => r.nome).join(', ') || '—';
        body.querySelector('.resp-editor summary').textContent =
          `Responsáveis pela empresa (${marcados.length})`;
        document.getElementById('fichaMeta').textContent =
          `${atualizada.cnpj || '—'} · ${atualizada.regime || '—'} · Responsável: ${nomes}`;

        aviso.textContent = 'Carteira atualizada.';
        aviso.style.color = 'var(--green-text)';
        aviso.style.background = 'var(--green-bg)';
        aviso.hidden = false;
        setTimeout(() => { aviso.hidden = true; }, 2000);
      } catch (err) {
        chk.checked = !chk.checked;
        aviso.textContent = 'Não foi possível alterar: ' + err.message;
        aviso.style.color = '';
        aviso.style.background = '';
        aviso.hidden = false;
      }
    })
  );
}

function blocoSituacao(e) {
  const hoje = new Date().toISOString().slice(0, 10);
  const encerrado = e.situacao === 'encerrado';

  return `
    <details class="situacao-editor">
      <summary>Alterar situação do cliente</summary>
      <div class="situacao-form">
        <div>
          <label for="sitSituacao">Situação</label>
          <select class="cad-input" id="sitSituacao">
            <option value="ativo" ${e.situacao === 'ativo' ? 'selected' : ''}>Ativo</option>
            <option value="sem_movimento" ${e.situacao === 'sem_movimento' ? 'selected' : ''}>Sem movimento</option>
            <option value="encerrado" ${encerrado ? 'selected' : ''}>Ex-cliente</option>
          </select>
        </div>
        <div>
          <label for="sitDesde">Cliente desde</label>
          <input type="date" class="cad-input" id="sitDesde" value="${e.cliente_desde || ''}">
        </div>
        <div id="sitSaidaBloco" style="display:${encerrado ? '' : 'none'};">
          <label for="sitAte">Data de saída</label>
          <input type="date" class="cad-input" id="sitAte" value="${e.cliente_ate || hoje}">
        </div>
        <div id="sitMotivoBloco" style="flex-basis:100%;display:${encerrado ? '' : 'none'};">
          <label for="sitMotivo">Motivo da saída</label>
          <input type="text" class="cad-input" id="sitMotivo" value="${esc(e.motivo_saida || '')}"
                 placeholder="Encerrou atividade, migrou de escritório, inadimplência...">
        </div>
        <button class="btn primary" type="button" id="sitSalvar">Aplicar</button>
      </div>
      <p class="situacao-aviso" id="sitAviso" hidden></p>
    </details>`;
}

function ligarSituacao(empresa, body) {
  const seletor = body.querySelector('#sitSituacao');
  const blocoSaida = body.querySelector('#sitSaidaBloco');
  const blocoMotivo = body.querySelector('#sitMotivoBloco');
  const aviso = body.querySelector('#sitAviso');

  seletor.addEventListener('change', () => {
    const encerrando = seletor.value === 'encerrado';
    blocoSaida.style.display = encerrando ? '' : 'none';
    blocoMotivo.style.display = encerrando ? '' : 'none';
    aviso.hidden = true;
  });

  body.querySelector('#sitSalvar').addEventListener('click', async (ev) => {
    const botao = ev.currentTarget;
    const situacao = seletor.value;
    const desde = body.querySelector('#sitDesde').value || null;

    botao.disabled = true;
    try {
      if (situacao === 'encerrado') {
        const data = body.querySelector('#sitAte').value;
        const motivo = body.querySelector('#sitMotivo').value.trim() || null;

        if (!data) {
          aviso.textContent = 'Informe a data de saída.';
          aviso.hidden = false;
          botao.disabled = false;
          return;
        }

        // Ensaio antes de executar: encerrar apaga competências, então a
        // pessoa vê quantas são — e quantas tinham trabalho — antes de decidir.
        const previa = await EmpresasData.encerrar(empresa.id, { data, motivo, confirmar: false });

        const detalhe = previa.removidas
          ? `\n\nSerão apagadas ${previa.removidas} competência(s) posteriores a ${dataBR(data)}` +
            (previa.com_trabalho
              ? `, sendo ${previa.com_trabalho} com trabalho já registrado.`
              : '.')
          : '\n\nNenhuma competência será apagada.';

        const ok = confirm(
          `Marcar ${empresa.nome} como ex-cliente desde ${dataBR(data)}?` +
          detalhe +
          '\n\nO histórico do que já foi feito permanece.'
        );
        if (!ok) { botao.disabled = false; return; }

        await EmpresasData.encerrar(empresa.id, { data, motivo, confirmar: true });

        if (desde !== (empresa.cliente_desde || null)) {
          await EmpresasData.alterarSituacao(empresa.id, {
            situacao: 'encerrado', cliente_desde: desde, cliente_ate: data, motivo_saida: motivo,
          });
        }
      } else {
        await EmpresasData.alterarSituacao(empresa.id, {
          situacao, cliente_desde: desde, cliente_ate: null, motivo_saida: null,
        });
      }

      await recarregarFechamento();
      await aoMudarDados();
      abrirFicha(empresa.id, 'cadastro');
    } catch (err) {
      aviso.textContent = 'Não foi possível alterar: ' + err.message;
      aviso.hidden = false;
      botao.disabled = false;
    }
  });
}

function renderAba(e, aba) {
  const body = document.getElementById('fichaBody');

  /*
   * Todas as abas de cadastro passam por aqui antes de desenhar: se a
   * pessoa clicou em Editar, mostra o formulário e nem chega no bloco de
   * leitura. Um caminho só para as seis abas.
   */
  if (editando.has(aba)) {
    if (!podeEditar(e, usuario())) { editando.delete(aba); }
    else {
      body.innerHTML = formularioAba(e, aba);
      ligarEdicao(e, aba, body);
      return;
    }
  }
  const podeMexer = podeEditar(e, usuario()) && CAMPOS_EDITAVEIS[aba];
  const responsaveis = (e.responsaveis || []).map((r) => r.nome).join(', ') || '—';
    // Agência e conta entram porque a MDJ tem três Bradesco: sem isso o
  // colaborador não sabe de qual conta está falando.
  const bancos = (e.bancos || [])
    .map((b) => b.nome + (b.agencia ? ' (Ag ' + b.agencia + ' · ' + (b.conta || '—') + ')' : ''))
    .join('  ·  ') || '—';

  if (aba === 'cadastro') {
    const sit = SITUACOES[e.situacao] || SITUACOES.ativo;
    const editavel = podeEditar(e, usuario());

    const filiais = filiaisDe(e.id);

    body.innerHTML = `
      ${e.matriz_id ? `
        <div class="situacao-faixa filial">
          <span class="tag blue">Filial</span>
          <span>O fechamento contábil desta empresa é feito na matriz
            <b>${esc(e.matriz?.nome || '—')}</b>.</span>
        </div>` : ''}
      ${filiais.length ? `
        <div class="situacao-faixa matriz">
          <span class="tag blue">Matriz</span>
          <span>Responde pelo contábil de <b>${filiais.length} filial(is)</b>:
            ${filiais.map((x) => esc(x.nome)).join(' · ')}</span>
        </div>` : ''}
      <div class="situacao-faixa ${e.situacao || 'ativo'}">
        <span class="tag ${sit.tag}">${sit.rotulo}</span>
        <span>Cliente de <b>${dataBR(e.cliente_desde)}</b> até <b>${e.cliente_ate ? dataBR(e.cliente_ate) : 'hoje'}</b></span>
        ${e.motivo_saida ? `<span class="situacao-motivo">${esc(e.motivo_saida)}</span>` : ''}
      </div>

      <div class="field-grid">
        ${campo('Código da empresa', e.codigo)}${campo('CNPJ', e.cnpj)}
        ${campo('Nome da empresa', e.nome)}${campo('Regime tributário', e.regime)}
        ${campo('Orientação de ritmo', e.periodicidade)}
        ${campo('Responsável pelos documentos', responsaveis)}${campo('E-mail do responsável', e.email)}
        ${campo('Nome salvo no Zappy Contábil', e.zappy)}
      </div>

      ${podeMexer ? botaoEditar('cadastro') : ''}
      ${ehGestao() ? blocoResponsaveis(e) : ''}
      ${editavel ? blocoSituacao(e) : ''}`;

    if (ehGestao()) ligarResponsaveis(e, body);
    if (editavel) ligarSituacao(e, body);
    ligarEdicao(e, 'cadastro', body);
  } else if (aba === 'pessoal') {
    body.innerHTML = `<div class="field-grid">
      ${campo('Possui funcionários?', simNao(e.funcionarios))}
      ${campo('Faz folha conosco?', simNao(e.folha_conosco))}
      ${campo('Possui somente pró-labore?', simNao(e.pro_labore_somente))}
    </div>
    ${podeMexer ? botaoEditar('pessoal') : ''}`;
    ligarEdicao(e, 'pessoal', body);
  } else if (aba === 'open') {
    body.innerHTML = `<div class="field-grid">
      ${campo('Está no Open?', simNao(e.open_finance))}${campo('Layout', e.layout)}
      ${campo('Qtd. de bancos/contas', e.qtd_contas)}
      ${campo('Possui aplicações?', simNao(e.aplicacoes))}
      ${campo('Contas no Open', e.contas_no_open)}
      <div class="field field-note">${campo('Contas fora do Open', e.contas_fora_open)}</div>
    </div>
    ${podeMexer ? botaoEditar('open') : ''}
    ${blocoContas(e)}`;
    ligarContas(e, body);
    ligarEdicao(e, 'open', body);
  } else if (aba === 'documentacao') {
    body.innerHTML = `<div class="field-grid">
      ${campo('Situação', e.situacao_doc)}
      ${campo('Última cobrança', e.ultima_cobranca ? new Date(e.ultima_cobranca + 'T00:00').toLocaleDateString('pt-BR') : '—')}
      ${campo('Bancos cadastrados', bancos)}
    </div>
    <p style="font-size:12px;color:var(--text-muted);margin-top:12px;">
      O acompanhamento mês a mês dos extratos está na aba Documentos.
    </p>
    ${podeMexer ? botaoEditar('documentacao') : ''}`;
    ligarEdicao(e, 'documentacao', body);
  } else if (aba === 'andamento') {
    body.innerHTML = `<div class="field-grid">
      ${campo('Trabalho realizado até', competenciaExtenso(e.realizado_ate))}
      ${campo('Competência em aberto', competenciaExtenso(e.competencia_aberta))}
      ${campo('Responsáveis', responsaveis)}
      ${campo('Status atual', e.status_atual)}
      <div class="field field-note">${campo('Observação', e.andamento)}</div>
    </div>
    <div id="fichaFrentes"><p class="ind-carregando">Lendo o checklist…</p></div>
    ${podeMexer ? botaoEditar('andamento') : ''}`;
    ligarEdicao(e, 'andamento', body);
    renderFrentes(e);
  } else if (aba === 'particularidades') {
    body.innerHTML = `<div class="particularidades">${esc(e.particularidades || 'Sem particularidades registradas.')}</div>
    ${podeMexer ? botaoEditar('particularidades') : ''}`;
    ligarEdicao(e, 'particularidades', body);
  } else if (aba === 'fechamento') {
    renderFechamento(e, body);
  }
}

/* ------------------------------------------------------------------ */
/* Feito até, por frente                                               */
/*                                                                     */
/* Observação dela: a pessoa preenche o checklist e DEPOIS avisa "essa  */
/* empresa está realizada até tal mês, a conciliação até tal data". São */
/* a mesma informação, e a segunda vez vai por recado — que não fica    */
/* registrado.                                                          */
/*                                                                      */
/* O dado já estava nos itens de conferência. "Até" conta só a          */
/* sequência sem buraco: OK em jan, fev, mar, furo em abr e OK de novo  */
/* em ago quer dizer feito até MARÇO, que é o que a pessoa diria.       */
/* ------------------------------------------------------------------ */

async function renderFrentes(e) {
  const alvo = document.getElementById('fichaFrentes');
  if (!alvo) return;

  let linhas;
  try {
    linhas = await EmpresasData.frentesDaEmpresa(e.id);
  } catch (err) {
    alvo.innerHTML = `<p class="frentes-vazio">Não consegui ler o checklist: ${esc(err.message)}</p>`;
    return;
  }

  // A ficha pode ter mudado de aba enquanto a consulta ia e voltava.
  if (!document.getElementById('fichaFrentes')) return;

  const porNome = new Map((linhas || []).map((l) => [l.frente, l]));
  const ordem = EmpresasData.FRENTES.filter((f) => porNome.has(f))
    .concat((linhas || []).map((l) => l.frente).filter((f) => !EmpresasData.FRENTES.includes(f)));

  if (!ordem.length) {
    alvo.innerHTML = `<div class="frentes">
      <h4>Feito até, por frente</h4>
      <p class="frentes-vazio">Esta empresa ainda não tem competência aberta.</p>
    </div>`;
    return;
  }

  alvo.innerHTML = `
    <div class="frentes">
      <h4>Feito até, por frente</h4>
      <p class="frentes-ajuda">Lido do checklist, sem ninguém digitar de novo.
        Conta só a sequência sem buraco — é o mês a partir do qual alguém precisa retomar.</p>
      <div class="frentes-grade">
        ${ordem.map((nome) => {
          const l = porNome.get(nome) || {};
          const emDia = !l.primeiro_aberto;
          return `
            <div class="frente ${l.feita_ate ? (emDia ? 'ok' : 'parcial') : 'zero'}">
              <span class="frente-nome">${esc(nome)}</span>
              <span class="frente-mes">${l.feita_ate ? competenciaExtenso(l.feita_ate) : 'não começou'}</span>
              ${l.primeiro_aberto
                ? `<span class="frente-retomar">retomar em ${competenciaExtenso(l.primeiro_aberto)}</span>`
                : l.feita_ate
                  ? '<span class="frente-retomar">nada em aberto</span>'
                  : '<span class="frente-retomar">sem competência registrada</span>'}
              ${Number(l.ok_fora_de_ordem) > 0
                ? `<span class="frente-salto" title="Meses marcados como OK depois do buraco — trabalho feito fora de ordem, que não conta para o &quot;até&quot;.">
                     ${l.ok_fora_de_ordem} mês(es) fora de ordem</span>`
                : ''}
            </div>`;
        }).join('')}
      </div>
    </div>`;
}

/* ------------------------------------------------------------------ */
/* Editar a ficha                                                      */
/*                                                                     */
/* As abas de cadastro nasceram só de leitura: os dados vinham da       */
/* planilha e a ficha era a consulta. Só que a planilha saiu de cena e  */
/* agora um telefone novo, uma folha que passou a ser feita aqui ou uma */
/* particularidade nova não tinham onde ser corrigidos — dependiam de   */
/* mim mexer no banco.                                                 */
/*                                                                     */
/* Um mecanismo só para todas as abas: cada uma declara seus campos, o  */
/* botão Editar troca a leitura pelo formulário e o Salvar grava só o   */
/* que mudou. Quem edita é quem já podia — gestão ou responsável pela   */
/* empresa — e a RLS confirma no banco.                                */
/* ------------------------------------------------------------------ */

const REGIMES = ['Simples Nacional', 'Lucro Presumido', 'Lucro Real',
                 'Doméstica', 'Imune/Isenta', 'MEI', 'Rural'];

const CAMPOS_EDITAVEIS = {
  cadastro: [
    { campo: 'codigo', rotulo: 'Código da empresa' },
    { campo: 'cnpj', rotulo: 'CNPJ' },
    { campo: 'nome', rotulo: 'Nome da empresa', obrigatorio: true },
    { campo: 'regime', rotulo: 'Regime tributário', tipo: 'opcao', opcoes: REGIMES },
    { campo: 'periodicidade', rotulo: 'Orientação de ritmo', tipo: 'opcao',
      opcoes: ['mensal', 'trimestral', 'anual'] },
    { campo: 'email', rotulo: 'E-mail do responsável' },
    { campo: 'zappy', rotulo: 'Nome salvo no Zappy Contábil' },
  ],
  pessoal: [
    { campo: 'funcionarios', rotulo: 'Possui funcionários?', tipo: 'simnao' },
    { campo: 'folha_conosco', rotulo: 'Faz folha conosco?', tipo: 'simnao' },
    { campo: 'pro_labore_somente', rotulo: 'Possui somente pró-labore?', tipo: 'simnao' },
  ],
  open: [
    { campo: 'open_finance', rotulo: 'Está no Open?', tipo: 'simnao' },
    { campo: 'layout', rotulo: 'Layout' },
    { campo: 'qtd_contas', rotulo: 'Qtd. de bancos/contas', tipo: 'numero' },
    { campo: 'aplicacoes', rotulo: 'Possui aplicações?', tipo: 'simnao' },
    { campo: 'contas_no_open', rotulo: 'Contas no Open' },
    { campo: 'contas_fora_open', rotulo: 'Contas fora do Open', tipo: 'texto_longo' },
  ],
  documentacao: [
    { campo: 'situacao_doc', rotulo: 'Situação' },
  ],
  andamento: [
    { campo: 'status_atual', rotulo: 'Status atual' },
    { campo: 'andamento', rotulo: 'Observação', tipo: 'texto_longo' },
  ],
  particularidades: [
    { campo: 'particularidades', rotulo: 'Particularidades', tipo: 'texto_longo', linhas: 10 },
  ],
};

/** Abas que a pessoa está editando agora — some quando a ficha fecha. */
let editando = new Set();

function botaoEditar(aba) {
  return `<div class="ficha-acoes">
    <button type="button" class="btn btn-editar-aba" data-aba="${aba}">
      ${svg('edit', 14)} Editar
    </button>
  </div>`;
}

function entradaDe(e, f) {
  const v = e[f.campo];
  const id = `ed_${f.campo}`;

  if (f.tipo === 'simnao') {
    return `<select class="cad-input" id="${id}">
      <option value="sim" ${v ? 'selected' : ''}>Sim</option>
      <option value="nao" ${v ? '' : 'selected'}>Não</option>
    </select>`;
  }
  if (f.tipo === 'opcao') {
    // Valor fora da lista acontece (regime digitado à mão na planilha).
    // Entra como opção para não sumir sem a pessoa perceber.
    const lista = f.opcoes.includes(v) || !v ? f.opcoes : [v, ...f.opcoes];
    return `<select class="cad-input" id="${id}">
      <option value="">—</option>
      ${lista.map((o) => `<option ${o === v ? 'selected' : ''}>${esc(o)}</option>`).join('')}
    </select>`;
  }
  if (f.tipo === 'texto_longo') {
    return `<textarea class="cad-input" id="${id}" rows="${f.linhas || 4}">${esc(v ?? '')}</textarea>`;
  }
  if (f.tipo === 'numero') {
    return `<input class="cad-input" id="${id}" type="number" min="0" value="${v ?? ''}">`;
  }
  return `<input class="cad-input" id="${id}" value="${esc(v ?? '')}">`;
}

function formularioAba(e, aba) {
  const campos = CAMPOS_EDITAVEIS[aba] || [];

  return `
    <div class="ficha-form">
      ${campos.map((f) => `
        <div class="ficha-form-campo ${f.tipo === 'texto_longo' ? 'largo' : ''}">
          <label for="ed_${f.campo}">${esc(f.rotulo)}</label>
          ${entradaDe(e, f)}
        </div>`).join('')}
      <div class="ficha-form-acoes">
        <button type="button" class="btn" id="edCancelar">Cancelar</button>
        <button type="button" class="btn primary" id="edSalvar" data-aba="${aba}">Salvar</button>
      </div>
      <p class="ficha-form-erro" id="edErro" hidden></p>
    </div>`;
}

function ligarEdicao(e, aba, body) {
  body.querySelectorAll('.btn-editar-aba').forEach((b) =>
    b.addEventListener('click', () => { editando.add(b.dataset.aba); renderAba(e, b.dataset.aba); })
  );

  const cancelar = body.querySelector('#edCancelar');
  if (cancelar) {
    cancelar.addEventListener('click', () => { editando.delete(aba); renderAba(e, aba); });
  }

  const salvar = body.querySelector('#edSalvar');
  if (salvar) salvar.addEventListener('click', () => salvarAba(e, aba, body, salvar));
}

async function salvarAba(e, aba, body, botao) {
  const erro = body.querySelector('#edErro');
  erro.hidden = true;

  // Só o que mudou vai para o banco — assim uma aba não sobrescreve o que
  // outra pessoa acabou de mexer em outro campo.
  const mudou = {};
  for (const f of CAMPOS_EDITAVEIS[aba] || []) {
    const el = body.querySelector(`#ed_${f.campo}`);
    if (!el) continue;

    let valor;
    if (f.tipo === 'simnao') valor = el.value === 'sim';
    else if (f.tipo === 'numero') valor = el.value === '' ? null : Number(el.value);
    else valor = el.value.trim() === '' ? null : el.value.trim();

    if (f.obrigatorio && (valor === null || valor === '')) {
      erro.textContent = `${f.rotulo} não pode ficar em branco.`;
      erro.hidden = false;
      return;
    }
    if ((e[f.campo] ?? null) !== valor) mudou[f.campo] = valor;
  }

  if (!Object.keys(mudou).length) {
    editando.delete(aba);
    renderAba(e, aba);
    return;
  }

  botao.disabled = true;
  botao.textContent = 'Salvando…';

  try {
    const linha = await EmpresasData.atualizar(e.id, mudou);
    // A linha que volta não traz responsáveis nem bancos; o Object.assign
    // só toca nas colunas devolvidas, então esses ficam de pé.
    Object.assign(e, linha || mudou);
    editando.delete(aba);
    renderAba(e, aba);
    aoMudarDados();
  } catch (falha) {
    botao.disabled = false;
    botao.textContent = 'Salvar';
    erro.textContent = falha.message.includes('row-level security')
      ? 'Você não é responsável por esta empresa, então não pode alterar a ficha dela.'
      : `Não consegui salvar: ${falha.message}`;
    erro.hidden = false;
  }
}

/* ------------------------------------------------------------------ */
/* Aba Fechamento                                                      */
/* ------------------------------------------------------------------ */

async function renderFechamento(empresa, body) {
  const editavel = podeEditar(empresa, usuario());
  body.innerHTML = `<p style="font-size:13px;color:var(--text-muted);">Carregando fechamento…</p>`;

  competencias = await Fechamento.listarFechamentos(empresa.id);
  competencias.sort((a, b) => a.competencia.localeCompare(b.competencia)); // antigas primeiro

  if (!competencias.length) {
    body.innerHTML = `
      <div class="fblock-title" style="margin-bottom:10px;">Nenhuma competência aberta</div>
      <p style="font-size:13px;color:var(--text-muted);margin-bottom:14px;">
        Abra as competências desta empresa para começar o fechamento.
        Ela é <b>${esc(empresa.periodicidade)}</b>.
      </p>
      ${editavel ? blocoAbrir(empresa) : avisoSemPermissao()}`;
    if (editavel) ligarAbrir(empresa, body);
    return;
  }

  // Por padrão já vem tudo que está em aberto — que é o caso de quem pega
  // a empresa atrasada e vai fazer janeiro a agosto de uma vez.
  const abertas = competencias.filter((c) => c.status !== 'concluido');
  selecionadas = new Set((abertas.length ? abertas : [competencias.at(-1)]).map((c) => c.id));

  await desenhar(empresa, body, editavel);
}

async function desenhar(empresa, body, editavel) {
  const ids = [...selecionadas];
  const [grupos, contagem, etapas] = await Promise.all([
    Fechamento.itensAgrupados(ids),
    Fechamento.marcacoesAgrupadas(ids),
    Fechamento.checklistPadrao(),
  ]);

  const comentarios = await Comentarios.listar(grupos.flatMap((g) => g.ocorrencias.map((o) => o.id)));
  const total = dados.checklistTotal;
  const feitosEmTodas = [...contagem.values()].filter((n) => n === ids.length).length;
  const ultimo = ultimaSelecionada();

  body.innerHTML = `
    ${blocoSeletor(editavel)}
    ${editavel ? blocoAbrir(empresa) : ''}

    <div class="fblock-title" style="margin:20px 0 4px;">Itens de conferência</div>
    ${ids.length > 1 ? `<p class="lote-aviso">
      Alterar o status vale para as <b>${ids.length} competências</b> selecionadas.
      A observação é gravada em <b>${competenciaExtenso(ultimo?.competencia)}</b>, o mês mais recente.
    </p>` : ''}
    <div id="fichaItens">${htmlItens(grupos, comentarios, editavel, ids.length)}</div>

    ${editavel ? `
      <div class="cad-item-row" style="margin-top:6px;">
        <input class="cad-input" id="novoItemNome" placeholder="Novo item (ex.: Fiscal, Banco Itaú...)">
        <button type="button" id="btnAddItem" class="btn">${svg('plus', 15)}</button>
      </div>` : ''}

    <div class="fblock-title" style="margin:22px 0 0;">Checklist padrão de fechamento</div>
    <div class="closing-progress">
      <div class="progress-track"><div class="progress-fill" style="width:${total ? (feitosEmTodas / total) * 100 : 0}%;"></div></div>
      <span style="font-size:12px;color:var(--text-secondary);">${feitosEmTodas} / ${total}${ids.length > 1 ? ' em todas' : ''}</span>
    </div>
    ${etapas.map((s) => `
      <p class="step-title">${s.ordem}. ${esc(s.titulo)}</p>
      ${s.itens.map((it) => {
        const n = contagem.get(it.id) || 0;
        const todas = n === ids.length;
        const algumas = n > 0 && !todas;
        return `<label class="check-row ${algumas ? 'parcial-lote' : ''}">
          <input type="checkbox" data-item="${it.id}" ${todas ? 'checked' : ''} ${editavel ? '' : 'disabled'}>
          ${esc(it.texto)}
          ${algumas ? `<span class="lote-contagem">${n} de ${ids.length}</span>` : ''}
        </label>`;
      }).join('')}`).join('')}

    ${editavel ? `<button class="btn primary" id="btnConcluir" style="width:100%;margin-top:20px;">
      Concluir ${ids.length > 1 ? `as ${ids.length} competências selecionadas` : 'esta competência'}
    </button>` : avisoSemPermissao()}`;

  ligarTudo(empresa, body, editavel, grupos);
}

function ultimaSelecionada() {
  return competencias.filter((c) => selecionadas.has(c.id)).at(-1);
}

/* ------------------------------------------------------------------ */
/* Blocos                                                              */
/* ------------------------------------------------------------------ */

function blocoSeletor(editavel) {
  const marcadas = selecionadas.size;
  return `
    <div class="fblock-title" style="margin-bottom:8px;">
      Competências
      <span style="font-weight:400;color:var(--text-muted);font-size:12px;">
        · ${marcadas} selecionada${marcadas === 1 ? '' : 's'}
      </span>
    </div>
    <div class="comp-chips">
      ${competencias.map((c) => `
        <button type="button" class="comp-chip ${selecionadas.has(c.id) ? 'on' : ''} ${c.status === 'concluido' ? 'ok' : ''}"
                data-comp="${c.id}" ${editavel ? '' : 'disabled'}
                title="${c.status === 'concluido' ? 'Concluída' : 'Em aberto'}">
          ${competenciaExtenso(c.competencia)}
        </button>`).join('')}
    </div>
    <div class="comp-atalhos">
      <button type="button" data-sel="abertas">Só as em aberto</button>
      <button type="button" data-sel="todas">Todas</button>
      <button type="button" data-sel="ultima">Só a mais recente</button>
    </div>`;
}

function blocoAbrir(empresa) {
  const ano = new Date().getFullYear();
  const mesAtual = String(new Date().getMonth() + 1).padStart(2, '0');
  return `
    <details class="abrir-comp">
      <summary>Abrir novas competências</summary>
      <div style="display:flex;gap:8px;align-items:flex-end;flex-wrap:wrap;padding:11px 0 2px;">
        <div><p style="font-size:11px;font-weight:600;margin:0 0 4px;">De</p>
          <input type="month" class="cad-input" id="compDe" value="${ano}-01" style="width:150px;"></div>
        <div><p style="font-size:11px;font-weight:600;margin:0 0 4px;">Até</p>
          <input type="month" class="cad-input" id="compAte" value="${ano}-${mesAtual}" style="width:150px;"></div>
        <button class="btn primary" id="btnAbrirComp" type="button">Abrir</button>
        <p style="flex-basis:100%;font-size:11.5px;color:var(--text-muted);margin:6px 0 0;">
          Cria todos os meses que faltam no período e copia os itens da última
          competência. Marque depois só o que foi realizado.
        </p>
      </div>
    </details>`;
}

function avisoSemPermissao() {
  return `<p style="font-size:12.5px;color:var(--text-muted);margin-top:14px;padding:10px 12px;background:var(--surface-2);border-radius:8px;">
    Você não é responsável por esta empresa, então vê os dados mas não edita.
    Se precisar assumir, peça à gestão para te incluir como responsável.
  </p>`;
}

const STATUS_ROTULO = { ok: 'OK', parcial: 'Parcial', pendente: 'Pendente', misto: 'Misto' };

function htmlItens(grupos, comentarios, editavel, qtdSelecionadas) {
  if (!grupos.length) {
    return `<p style="font-size:13px;color:var(--text-muted);">Nenhum item de conferência nas competências selecionadas.</p>`;
  }

  return grupos.map((g) => {
    const idsDoGrupo = g.ocorrencias.map((o) => o.id);
    const meus = comentarios.filter((c) => idsDoGrupo.includes(c.item_id));
    const abertos = meus.filter((c) => !c.resolvido).length;

    return `
    <div class="fitem" data-nome="${esc(g.nome)}" style="border:1px solid var(--border);border-radius:var(--radius-sm);padding:11px 13px;margin-bottom:8px;">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
        <span style="font-size:13px;font-weight:500;">${esc(g.nome)}</span>
        <select class="fitem-status" ${editavel ? '' : 'disabled'}
                style="border:1px solid var(--border);border-radius:100px;padding:5px 10px;font-size:12px;font-family:inherit;background:var(--surface);color:var(--text-primary);">
          ${g.status === 'misto' ? '<option value="misto" selected>Misto</option>' : ''}
          <option value="ok" ${g.status === 'ok' ? 'selected' : ''}>OK</option>
          <option value="parcial" ${g.status === 'parcial' ? 'selected' : ''}>Parcial</option>
          <option value="pendente" ${g.status === 'pendente' ? 'selected' : ''}>Pendente</option>
        </select>
      </div>

      ${g.status === 'misto' ? `<p class="lote-detalhe">${
        g.ocorrencias.map((o) => `${competenciaExtenso(compDe(o.fechamento_id))}: ${STATUS_ROTULO[o.status]}`).join(' · ')
      }</p>` : ''}

      <textarea class="fitem-obs" ${editavel ? '' : 'disabled'}
        placeholder="O que falta ou o que aconteceu..."
        style="width:100%;margin-top:8px;display:${g.status !== 'ok' ? 'block' : 'none'};border:1px solid var(--border);border-radius:6px;padding:7px 9px;font-size:12px;font-family:inherit;min-height:52px;background:var(--surface-2);color:var(--text-primary);">${esc(g.obs || '')}</textarea>

      <div class="coment-barra">
        <button type="button" class="coment-toggle" data-nome="${esc(g.nome)}">
          ${svg('book', 13)} Comentários${meus.length ? ` (${meus.length})` : ''}
          ${abertos ? `<span class="tag red" style="margin-left:6px;">${abertos} em aberto</span>` : ''}
        </button>
      </div>
      <div class="coment-lista" hidden>
        ${meus.length ? meus.map((c) => htmlComentario(c)).join('')
                      : '<p class="coment-vazio">Nenhum comentário neste item ainda.</p>'}
        ${editavel || true ? htmlNovoComentario(idsDoGrupo.at(-1)) : ''}
      </div>
    </div>`;
  }).join('');
}

function compDe(fechamentoId) {
  return competencias.find((c) => c.id === fechamentoId)?.competencia;
}

function htmlComentario(c) {
  const t = Comentarios.TIPOS[c.tipo] || Comentarios.TIPOS.orientacao;
  const data = new Date(c.criado_em);
  return `
    <div class="coment ${c.resolvido ? 'resolvido' : ''}" data-id="${c.id}">
      <div class="coment-topo">
        <span class="tag ${t.tag}">${t.rotulo}</span>
        <span class="coment-autor">${esc(c.autor?.nome || '—')}</span>
        <span class="coment-data">${data.toLocaleDateString('pt-BR')} ${data.toTimeString().slice(0, 5)}</span>
      </div>
      <p class="coment-texto">${esc(c.texto)}</p>
      <label class="coment-resolver">
        <input type="checkbox" data-resolver="${c.id}" ${c.resolvido ? 'checked' : ''}>
        ${c.resolvido ? `Resolvido${c.resolvedor ? ' por ' + esc(c.resolvedor.nome) : ''}` : 'Marcar como resolvido'}
      </label>
    </div>`;
}

function htmlNovoComentario(itemId) {
  return `
    <div class="coment-novo" data-item="${itemId}">
      <select class="cad-input coment-tipo">
        <option value="orientacao">Orientação</option>
        <option value="correcao">Correção</option>
        <option value="duvida">Dúvida</option>
      </select>
      <textarea class="cad-input coment-texto-novo" placeholder="Explique o que aconteceu e o que precisa melhorar..."></textarea>
      <button type="button" class="btn primary coment-enviar">Comentar</button>
    </div>`;
}

/* ------------------------------------------------------------------ */
/* Eventos                                                             */
/* ------------------------------------------------------------------ */

function ligarAbrir(empresa, body) {
  body.querySelector('#btnAbrirComp')?.addEventListener('click', async (ev) => {
    const botao = ev.currentTarget;
    const de = body.querySelector('#compDe').value;
    const ate = body.querySelector('#compAte').value;
    if (!de || !ate) return;

    botao.disabled = true;
    botao.textContent = 'Abrindo…';
    try {
      const criadas = await Fechamento.abrirCompetencias(empresa.id, `${de}-01`, `${ate}-01`);
      await recarregarFechamento();
      await aoMudarDados();
      abrirFicha(empresa.id, 'fechamento');
      if (!criadas) alert('Nenhuma competência nova — todas já existiam nesse intervalo.');
    } catch (err) {
      alert('Não foi possível abrir as competências: ' + err.message);
      botao.disabled = false;
      botao.textContent = 'Abrir';
    }
  });
}

function ligarTudo(empresa, body, editavel, grupos) {
  ligarAbrir(empresa, body);

  // --- seleção de competências ---
  body.querySelectorAll('.comp-chip').forEach((chip) =>
    chip.addEventListener('click', () => {
      const id = chip.dataset.comp;
      if (selecionadas.has(id)) selecionadas.delete(id);
      else selecionadas.add(id);
      if (!selecionadas.size) selecionadas.add(id); // nunca deixa vazio
      desenhar(empresa, body, editavel);
    })
  );

  body.querySelectorAll('.comp-atalhos button').forEach((btn) =>
    btn.addEventListener('click', () => {
      const modo = btn.dataset.sel;
      if (modo === 'todas') selecionadas = new Set(competencias.map((c) => c.id));
      else if (modo === 'ultima') selecionadas = new Set([competencias.at(-1).id]);
      else {
        const abertas = competencias.filter((c) => c.status !== 'concluido');
        selecionadas = new Set((abertas.length ? abertas : [competencias.at(-1)]).map((c) => c.id));
      }
      desenhar(empresa, body, editavel);
    })
  );

  // --- comentários (qualquer colaborador pode comentar) ---
  body.querySelectorAll('.coment-toggle').forEach((btn) =>
    btn.addEventListener('click', () => {
      const lista = btn.closest('.fitem').querySelector('.coment-lista');
      lista.hidden = !lista.hidden;
    })
  );

  body.querySelectorAll('.coment-enviar').forEach((btn) =>
    btn.addEventListener('click', async () => {
      const caixa = btn.closest('.coment-novo');
      const texto = caixa.querySelector('.coment-texto-novo').value.trim();
      if (!texto) return;
      btn.disabled = true;
      try {
        await Comentarios.criar({
          itemId: caixa.dataset.item,
          tipo: caixa.querySelector('.coment-tipo').value,
          texto,
        }, usuario().id);
        await desenhar(empresa, body, editavel);
      } catch (err) {
        alert('Não foi possível comentar: ' + err.message);
        btn.disabled = false;
      }
    })
  );

  body.querySelectorAll('[data-resolver]').forEach((chk) =>
    chk.addEventListener('change', async () => {
      try {
        await Comentarios.marcarResolvido(chk.dataset.resolver, chk.checked, usuario().id);
        await desenhar(empresa, body, editavel);
      } catch (err) {
        chk.checked = !chk.checked;
        alert('Não foi possível atualizar: ' + err.message);
      }
    })
  );

  if (!editavel) return;

  const ids = [...selecionadas];
  const idUltimo = ultimaSelecionada()?.id;

  // --- itens de conferência, em lote ---
  body.querySelectorAll('.fitem').forEach((linha) => {
    const nome = linha.dataset.nome;
    const select = linha.querySelector('.fitem-status');
    const obs = linha.querySelector('.fitem-obs');
    const grupo = grupos.find((g) => g.nome === nome);

    select.addEventListener('change', async () => {
      if (select.value === 'misto') return;
      obs.style.display = select.value !== 'ok' ? 'block' : 'none';
      try {
        await Fechamento.alterarItemEmLote({
          nome, fechamentoIds: ids, status: select.value,
          obs: obs.value || null, fechamentoIdObs: idUltimo,
        }, usuario().id);
        await recarregarFechamento();
        await aoMudarDados();
        await desenhar(empresa, body, editavel);
      } catch (err) {
        alert('Não foi possível salvar: ' + err.message);
      }
    });

    let espera;
    obs.addEventListener('input', () => {
      clearTimeout(espera);
      espera = setTimeout(async () => {
        // A observação vai só para o mês mais recente selecionado.
        const alvo = grupo?.ocorrencias.find((o) => o.fechamento_id === idUltimo);
        try {
          if (alvo) await Fechamento.alterarItem(alvo.id, { obs: obs.value }, usuario().id);
        } catch (err) {
          console.error('Falha ao salvar observação:', err.message);
        }
      }, 700);
    });
  });

  // --- novo item ---
  body.querySelector('#btnAddItem')?.addEventListener('click', async () => {
    const input = body.querySelector('#novoItemNome');
    const nome = input.value.trim();
    if (!nome) return;
    try {
      await Fechamento.alterarItemEmLote({
        nome, fechamentoIds: ids, status: 'pendente', obs: null, fechamentoIdObs: idUltimo,
      }, usuario().id);
      input.value = '';
      await recarregarFechamento();
      await aoMudarDados();
      await desenhar(empresa, body, editavel);
    } catch (err) {
      alert('Não foi possível adicionar o item: ' + err.message);
    }
  });

  // --- checklist, em lote ---
  body.querySelectorAll('.check-row input').forEach((c) =>
    c.addEventListener('change', async () => {
      try {
        await Fechamento.marcarChecklistEmLote(ids, Number(c.dataset.item), c.checked, usuario().id);
        await recarregarFechamento();
        await aoMudarDados();
        await desenhar(empresa, body, editavel);
      } catch (err) {
        c.checked = !c.checked;
        alert('Não foi possível salvar: ' + err.message);
      }
    })
  );

  // --- concluir, com confirmação listando os meses ---
  body.querySelector('#btnConcluir')?.addEventListener('click', async () => {
    const alvos = competencias.filter((c) => selecionadas.has(c.id) && c.status !== 'concluido');
    if (!alvos.length) {
      alert('As competências selecionadas já estão concluídas.');
      return;
    }

    const pendentes = grupos.filter((g) => g.status !== 'ok');
    const aviso = pendentes.length
      ? `\n\nAtenção: ${pendentes.length} item(ns) ainda não estão OK:\n` +
        pendentes.map((g) => `  · ${g.nome} (${STATUS_ROTULO[g.status]})`).join('\n')
      : '';

    const lista = alvos.map((c) => `  · ${competenciaExtenso(c.competencia)}`).join('\n');
    if (!confirm(`Concluir ${alvos.length} competência(s) de ${empresa.nome}?\n\n${lista}${aviso}`)) return;

    try {
      await Fechamento.concluirVarios(alvos.map((c) => c.id), usuario().id);
      await recarregarFechamento();
      await aoMudarDados();
      abrirFicha(empresa.id, 'fechamento');
    } catch (err) {
      alert('Não foi possível concluir: ' + err.message);
    }
  });
}
